import React from 'react';

// --- NEW: derive TP/SL from a group's activities (handles bracket orders + fallbacks)
// Pull TP/SL from a group's activities (prefers bracket â†’ explicit fields)
const deriveLevelsFromActivities = (groupActivities) => {
  let tp = 0, sl = 0;
  console.log("Activity:", groupActivities)
  for (const a of groupActivities) {
    // bracket first
    
    if (a.bracket) {
      const btp = a.bracket.take_profit_price ?? a.bracket.take_profit?.limit_price;
      const bsl = a.bracket.stop_loss_price ?? a.bracket.stop_loss?.stop_price;
      if (btp && +btp > 0) tp = +btp;
      if (bsl && +bsl > 0) sl = +bsl;
    }
    // explicit fields next (from BuildActivity passthrough)
    const candTP = a.target ?? a.tp ?? a.take_profit ?? a.target_price ?? a.tp_price ?? a.limit_price;
    const candSL = a.stop   ?? a.sl ?? a.stop_loss   ?? a.stop_price   ?? a.sl_price;
    if (candTP && +candTP > 0) tp = +candTP;
    if (candSL && +candSL > 0) sl = +candSL;
  }

  return { tp, sl };
};


/**
 * Helper to build position summary from grouped activities
 * Handles complex scenarios like: 0 â†’ 100 â†’ 200 â†’ 100 â†’ 0 (double orders)
 */
const buildPositionsFromActivities = (activities, currentPrice) => {
    if (!activities || activities.length === 0) {
        return [];
    }

    // Group activities by trade group
    const tradeGroups = {};
    activities.forEach((activity) => {
        const groupId = activity.group;
        if (!tradeGroups[groupId]) {
            tradeGroups[groupId] = [];
        }
        tradeGroups[groupId].push(activity);
    });

    const positions = [];

    Object.keys(tradeGroups).forEach((groupId) => {
        const groupActivities = tradeGroups[groupId].sort(
            (a, b) => new Date(a.created_at_gmt) - new Date(b.created_at_gmt)
        );

        const firstActivity = groupActivities[0];
        const lastActivity = groupActivities[groupActivities.length - 1];
        
        // CRITICAL: Always use the LAST activity's open_qty
        // This handles double orders: 0â†’100â†’200â†’100â†’0
        // The last activity shows the final state
        const openQty = lastActivity.open_qty || 0;
        
        // Detailed logging for debugging
        console.log(`ðŸ“Š Group ${groupId} (${firstActivity.symbol}):`);
        console.log(`   Activities: ${groupActivities.length}`);
        groupActivities.forEach((act, idx) => {
            console.log(`   ${idx + 1}. ${act.side} ${act.qty} @ $${act.price} â†’ open_qty: ${act.open_qty}`);
        });
        console.log(`   Final state: open_qty = ${openQty} â†’ ${openQty !== 0 ? 'ACTIVE' : 'CLOSED'}`);

        // Only include if position is still open (last activity shows non-zero)
        if (openQty !== 0) {
            const isLong = firstActivity.side === "buy";
            const entryPrice = parseFloat(firstActivity.price);
            const qty = Math.abs(openQty);
            const symbol = firstActivity.symbol;

            // Calculate unrealized P&L
            const unrealizedPL = currentPrice > 0
                ? isLong
                    ? (currentPrice - entryPrice) * qty
                    : (entryPrice - currentPrice) * qty
                : 0;

            // Calculate hold time
            const entryTime = new Date(firstActivity.created_at_gmt);
            const now = new Date();
            const holdTimeMin = Math.floor((now - entryTime) / 60000);
            const { tp, sl } = deriveLevelsFromActivities(groupActivities);

            positions.push({
                // Keep all activity fields
                ...lastActivity,
                // Position-specific fields
                id: lastActivity.id || lastActivity.a_id,
                a_id: lastActivity.a_id,
                group: groupId,
                symbol,
                isLong,
                side: isLong ? 'buy' : 'sell_short',
                entryPrice,
                currentPrice,
                price: entryPrice, // for compatibility
                qty,
                open_qty: openQty,
                unrealizedPL,
                entryTime: firstActivity.created_at_gmt,
                holdTimeMin,
                // Placeholders - will be enhanced with target/stop from orders
                target: tp || 0,
                stop: sl || 0,
            });
        }
    });

    console.log(`âœ… Built ${positions.length} active positions from ${activities.length} activities`);
    return positions;
};

// Helper function to extract adjustments from liveBars
const extractAdjustments = (liveBars, positionId) => {
    if (!liveBars || !Array.isArray(liveBars) || !positionId) {
        return { adjustments: [], count: 0, hasAdjustments: false };
    }

    const adjustments = [];
    
    liveBars.forEach((bar, index) => {
        // Try multiple fields to match position ID
        const barPositionId = bar.eval_position_id || bar.position_id || bar.a_id || bar.group;
        
        const isMatch = barPositionId == positionId; // Use == for type coercion
        
        if (!isMatch) {
            return;
        }
        
        // Check for ADJUST decision with new values
        if (bar.eval_decision === 'ADJUST') {
            const hasNewTarget = bar.eval_new_target !== undefined && bar.eval_new_target !== null && bar.eval_new_target > 0;
            const hasNewStop = bar.eval_new_stop_loss !== undefined && bar.eval_new_stop_loss !== null && bar.eval_new_stop_loss > 0;
            
            if (hasNewTarget || hasNewStop) {
                const adjustment = {
                    timestamp: bar.t,
                    newTarget: hasNewTarget ? parseFloat(bar.eval_new_target) : null,
                    newStop: hasNewStop ? parseFloat(bar.eval_new_stop_loss) : null,
                    reason: bar.eval_pattern_influence || bar.eval_reasoning || 'Adjustment'
                };
                adjustments.push(adjustment);
            }
        }
    });

    return {
        adjustments,
        count: adjustments.length,
        hasAdjustments: adjustments.length > 0
    };
};

// PositionCard Component with Adjustment Display
const PositionCard = ({ position, onClose, onAdjust, onDetail, currentPrice, liveBars }) => {
    if (!position) {
        return null;
    }

    const {
        symbol,
        side,
        isLong,
        qty,
        open_qty,
        price,
        entryPrice,
        currentPrice: positionCurrentPrice,
        target,
        stop,
        created_at_gmt,
        entryTime,
        group,
        id,
        a_id
    } = position;

    const current = currentPrice || positionCurrentPrice || price || 0;
    const entry = entryPrice || price || 0;
    const quantity = Math.abs(open_qty || qty || 0);
    const positionSide = isLong !== undefined ? (isLong ? 'LONG' : 'SHORT') : (side === 'buy' ? 'LONG' : 'SHORT');

    // Extract adjustments from liveBars
    const positionId = group || id || a_id; // Use group first as it's most reliable
    const { adjustments, count, hasAdjustments } = extractAdjustments(liveBars, positionId);
    
    // Get original values
    const origTargetValue = (
      target ??
      position.take_profit ??
      position.tp ??
      position.target_price ??
      position.tp_price ??
      position.limit_price ??
      0
    );
    
    const origStopValue = (
      stop ??
      position.stop_loss ??
      position.sl ??
      position.stop_price ??
      position.sl_price ??
      0
    );
    
    // Get current values (latest adjustment or original)
    let targetValue = origTargetValue;
    let stopValue = origStopValue;
    
    if (hasAdjustments && adjustments.length > 0) {
        const latestAdjustment = adjustments[adjustments.length - 1];
        if (latestAdjustment.newTarget !== null) {
            targetValue = latestAdjustment.newTarget;
        }
        if (latestAdjustment.newStop !== null) {
            stopValue = latestAdjustment.newStop;
        }
    }
    
    const targetAdjusted = hasAdjustments && (targetValue !== origTargetValue) && targetValue > 0;
    const stopAdjusted = hasAdjustments && (stopValue !== origStopValue) && stopValue > 0;

    // Calculate unrealized P&L
    let unrealizedPL = 0;
    if (positionSide === 'LONG') {
        unrealizedPL = (current - entry) * quantity;
    } else {
        unrealizedPL = (entry - current) * quantity;
    }

    const unrealizedPercent = entry !== 0
        ? ((unrealizedPL / (entry * quantity)) * 100)
        : 0;

    const isPositive = unrealizedPL >= 0;
    const sideClass = positionSide === 'LONG' ? 'long' : 'short';

    // Calculate hold time
    const startTime = new Date(entryTime || created_at_gmt);
    const holdTimeMs = Date.now() - startTime.getTime();
    const holdTimeMinutes = Math.floor(holdTimeMs / 60000);
    const holdTimeHours = Math.floor(holdTimeMinutes / 60);
    const holdTime = holdTimeHours > 0
        ? `${holdTimeHours}h ${holdTimeMinutes % 60}m`
        : `${holdTimeMinutes}m`;

    // Calculate risk/reward ratio (using current adjusted values)
    let riskReward = 'N/A';
    if (targetValue > 0 && stopValue > 0) {
        if (positionSide === 'LONG') {
            const reward = targetValue - entry;
            const risk = entry - stopValue;
            riskReward = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        } else {
            const reward = entry - targetValue;
            const risk = stopValue - entry;
            riskReward = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        }
    }

    // Calculate original risk/reward for comparison
    let originalRR = 'N/A';
    if (origTargetValue > 0 && origStopValue > 0) {
        if (positionSide === 'LONG') {
            const reward = origTargetValue - entry;
            const risk = entry - origStopValue;
            originalRR = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        } else {
            const reward = entry - origTargetValue;
            const risk = origStopValue - entry;
            originalRR = risk > 0 ? (reward / risk).toFixed(1) + 'R' : 'N/A';
        }
    }

    return (
        <div className={`position-card ${sideClass}`}>
            <style>{`
        .position-card {
          background: linear-gradient(135deg, #1a1a1a 0%, #161616 100%);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid #2a2a2a;
          transition: all 0.2s ease;
        }

        .position-card:hover {
          border-color: #3a3a3a;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        }

        .position-card.long {
          border-left: 3px solid #10b981;
        }

        .position-card.short {
          border-left: 3px solid #ef4444;
        }

        .position-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .position-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .side-badge {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          color: white;
        }

        .side-badge.long {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .side-badge.short {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }

        .symbol {
          font-size: 16px;
          font-weight: 700;
          color: #e0e0e0;
          letter-spacing: 0.5px;
        }

        .adjustment-badge {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          font-size: 9px;
          font-weight: 700;
          padding: 3px 6px;
          border-radius: 4px;
          letter-spacing: 0.3px;
        }

        .pnl {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .pnl-amount {
          font-size: 16px;
          font-weight: 700;
        }

        .pnl.positive .pnl-amount {
          color: #10b981 !important;
        }

        .pnl.negative .pnl-amount {
          color: #ef4444 !important;
        }

        .pnl-percent {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .pnl.positive .pnl-percent {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .pnl.negative .pnl-percent {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .position-details {
          margin-bottom: 12px;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .detail-row {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-label {
          font-size: 11px;
          color: #888;
          font-weight: 500;
        }

        .detail-value {
          font-size: 13px;
          color: #e0e0e0;
          font-weight: 600;
        }

        .position-levels {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 8px;
          padding: 10px;
          margin-bottom: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .level-row {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
        }

        .level-icon {
          font-size: 14px;
        }

        .level-label {
          color: #888;
          font-weight: 500;
          min-width: 30px;
        }

        .level-value-container {
          margin-left: auto;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .level-value {
          color: #e0e0e0;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .level-row.tp .level-value {
          color: #10b981;
        }

        .level-row.sl .level-value {
          color: #ef4444;
        }

        .original-value {
          font-size: 11px;
          color: #666;
          text-decoration: line-through;
          font-weight: 400;
        }

        .adjustment-arrow {
          color: #f59e0b;
          font-size: 10px;
          font-weight: 700;
        }

        .adjusted-value {
          font-size: 13px;
          font-weight: 700;
        }

        .level-row.tp .adjusted-value {
          color: #10b981;
        }

        .level-row.sl .adjusted-value {
          color: #ef4444;
        }

        .adjustment-info {
          font-size: 10px;
          color: #f59e0b;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .position-metrics {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
        }

        .metric {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .metric-label {
          font-size: 11px;
          color: #888;
          font-weight: 500;
        }

        .metric-value {
          font-size: 12px;
          color: #e0e0e0;
          font-weight: 600;
          padding: 3px 8px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
        }

        .metric-value.adjusted {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.3);
        }

        .rr-comparison {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
        }

        .rr-original {
          color: #666;
          text-decoration: line-through;
          font-weight: 400;
        }

        .position-actions {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px;
        }

        .action-btn {
          padding: 10px;
          border-radius: 8px;
          border: none;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .close-btn {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .close-btn:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
        }

        .adjust-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }

        .adjust-btn:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        }

        .detail-btn {
          background: rgba(255, 255, 255, 0.08);
          color: #e0e0e0;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .detail-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateY(-1px);
        }
      `}</style>

            <div className="position-header">
                <div className="position-title">
                    <span className={`side-badge ${sideClass}`}>
                        {positionSide === 'LONG' ? 'L' : 'S'}
                    </span>
                    <span className="symbol">{symbol}</span>
                    {hasAdjustments && (
                        <span className="adjustment-badge">
                            {count}Ã— ADJ
                        </span>
                    )}
                </div>
                <div className={`pnl ${isPositive ? 'positive' : 'negative'}`}>
                    <span className="pnl-amount" style={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                        {isPositive ? '+' : '-'}${Math.abs(unrealizedPL).toFixed(0)}
                    </span>
                    <span className="pnl-percent">
                        {isPositive ? '▲' : '▼'}{Math.abs(unrealizedPercent).toFixed(2)}%
                    </span>
                </div>
            </div>

            <div className="position-details">
                <div className="detail-grid">
                    <div className="detail-row">
                        <span className="detail-label">Qty:</span>
                        <span className="detail-value">{quantity}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Entry:</span>
                        <span className="detail-value">${parseFloat(entry).toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Current:</span>
                        <span className="detail-value">${parseFloat(current).toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {(targetValue > 0 || stopValue > 0) && (
                <div className="position-levels">
                    {targetValue > 0 && (
                        <div className="level-row tp">
                            <span className="level-icon">🎯</span>
                            <span className="level-label">TP:</span>
                            <div className="level-value-container">
                                {targetAdjusted ? (
                                    <>
                                        <div className="level-value">
                                            <span className="original-value">${parseFloat(origTargetValue).toFixed(2)}</span>
                                            <span className="adjustment-arrow">â†’</span>
                                            <span className="adjusted-value">${parseFloat(targetValue).toFixed(2)}</span>
                                        </div>
                                        <span className="adjustment-info">
                                            âš¡ Adjusted
                                        </span>
                                    </>
                                ) : (
                                    <div className="level-value">
                                        ${parseFloat(targetValue).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {stopValue > 0 && (
                        <div className="level-row sl">
                            <span className="level-icon">🛡️</span>
                            <span className="level-label">SL:</span>
                            <div className="level-value-container">
                                {stopAdjusted ? (
                                    <>
                                        <div className="level-value">
                                            <span className="original-value">${parseFloat(origStopValue).toFixed(2)}</span>
                                            <span className="adjustment-arrow">â†’</span>
                                            <span className="adjusted-value">${parseFloat(stopValue).toFixed(2)}</span>
                                        </div>
                                        <span className="adjustment-info">
                                            âš¡ Adjusted
                                        </span>
                                    </>
                                ) : (
                                    <div className="level-value">
                                        ${parseFloat(stopValue).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="position-metrics">
                <div className="metric">
                    <span className="metric-label">Risk:</span>
                    {(targetAdjusted || stopAdjusted) && originalRR !== riskReward ? (
                        <div className="rr-comparison">
                            <span className="rr-original">{originalRR}</span>
                            <span className="adjustment-arrow">â†’</span>
                            <span className={`metric-value adjusted`}>{riskReward}</span>
                        </div>
                    ) : (
                        <span className="metric-value">{riskReward}</span>
                    )}
                </div>
                <div className="metric">
                    <span className="metric-label">Hold:</span>
                    <span className="metric-value">{holdTime}</span>
                </div>
            </div>

            <div className="position-actions">
                <button className="action-btn close-btn" onClick={() => onClose && onClose(position)}>
                    Close
                </button>
                <button className="action-btn adjust-btn" onClick={() => onAdjust && onAdjust(position)}>
                    Adjust
                </button>
                <button className="action-btn detail-btn" onClick={() => onDetail && onDetail(position)}>
                    Detail
                </button>
            </div>
        </div>
    );
};

// ActivePositions Container Component - Uses activities directly
const ActivePositions = ({ 
    activities,      // NEW: Direct from buildActivities()
    positions,       // OLD: Keep for backward compatibility
    onClose, 
    onAdjust, 
    onDetail, 
    onAddPosition, 
    currentPrice, 
    liveBars 
}) => {
    // PREFER positions from TradingCommand (which has all enriched data)
    // Only rebuild from activities if positions not provided
    const activePositions = React.useMemo(() => {
        if (positions && positions.length > 0) {
            console.log('🔍 ActivePositions: Filtering from TradingCommand positions');
            console.log(`   Total positions received: ${positions.length}`);
            
            const filtered = positions.filter(p => {
                // CRITICAL: Check open_qty explicitly
                // Don't fall back to qty because open_qty=0 is valid for closed positions
                const openQty = p.open_qty ?? 0; // Use nullish coalescing to preserve 0
                const isActive = openQty !== 0;
                
                console.log(`   ${isActive ? '✅ KEEP' : '❌ FILTER'}: ${p.symbol} group=${p.group} open_qty=${p.open_qty} → ${isActive ? 'ACTIVE' : 'CLOSED'}`);
                
                return isActive;
            });
            
            console.log(`   📊 Result: ${filtered.length} active of ${positions.length} total positions`);
            return filtered;
        } else if (activities && activities.length > 0) {
            console.log('⚠️ Fallback: Building active positions from activities');
            return buildPositionsFromActivities(activities, currentPrice);
        }
        return [];
    }, [positions, activities, currentPrice]);


    const handleAddPosition = () => {
        if (onAddPosition) {
            onAddPosition();
        } else {
            console.log('Add new position');
        }
    };

    return (
        <div className="active-positions-container">
            <style>{`
        .active-positions-container {
          width: 100%;
          height: 100%;
          background: linear-gradient(180deg, #0a0a0a 0%, #121212 100%);
          padding: 16px;
          overflow-y: auto;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .active-positions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #2a2a2a;
          flex-shrink: 0;
        }

        .active-positions-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #e0e0e0;
          letter-spacing: 0.5px;
        }

        .position-count {
          background: #1e3a5f;
          color: #4a9eff;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .positions-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 16px;
          flex: 1;
          overflow-y: auto;
        }

        .add-position-btn {
          width: 100%;
          padding: 14px;
          background: rgba(74, 158, 255, 0.1);
          border: 2px dashed rgba(74, 158, 255, 0.3);
          border-radius: 8px;
          color: #4a9eff;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .add-position-btn:hover {
          background: rgba(74, 158, 255, 0.15);
          border-color: rgba(74, 158, 255, 0.5);
          transform: translateY(-1px);
        }

        .active-positions-container::-webkit-scrollbar {
          width: 6px;
        }

        .active-positions-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .active-positions-container::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }

        .active-positions-container::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .empty-state-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .empty-state-text {
          font-size: 14px;
          margin-bottom: 8px;
        }

        .empty-state-subtext {
          font-size: 12px;
          color: #555;
        }
      `}</style>

            <div className="active-positions-header">
                <h3>ACTIVE POSITIONS</h3>
                <span className="position-count">{activePositions.length}</span>
            </div>

            {activePositions.length > 0 ? (
                <div className="positions-list">
                    {activePositions.map((position, index) => (
                        <PositionCard
                            key={position.id || position.a_id || position.group || index}
                            position={position}
                            currentPrice={currentPrice}
                            liveBars={liveBars}
                            onClose={onClose}
                            onAdjust={onAdjust}
                            onDetail={onDetail}
                        />
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🗄️</div>
                    <div className="empty-state-text">No active positions</div>
                    <div className="empty-state-subtext">Add a position to start trading</div>
                </div>
            )}

            <button className="add-position-btn" onClick={handleAddPosition}>
                + Add Position
            </button>
        </div>
    );
};

export default ActivePositions;